import $ from 'jquery';

import { Component, ElementRef, Inject, AfterViewInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';

import { ConfirmPrompt, IConfirmPromptOptions } from '@app/services/confirm-prompt';
import { DashboardSettings } from '@app/services/dashboard-settings';

import { getLocale } from '@app/lib/i18n';
import { YELLOW_60 } from '@app/lib/colors';
import { LOCALE_MODAL_DASHBOARD_KEYS, seti18nLangCookie } from '@app/lib/boot-utils';
import { getCookie } from '@app/lib/cookies';
import { SUPPORTED_LANGUAGES, I18N_COOKIE_KEY } from '@app/lib/supported-languages';

export interface IIConType {
  iconColor: string;
  icon: string;
}

@Component({
  selector: 'confirm-prompt-modal',
  templateUrl: './confirm-prompt.component.html',
  styleUrls: ['./confirm-prompt.component.scss'],
})
export class ConfirmPromptModalComponent implements AfterViewInit {
  public message: string | TemplateRef<any>;
  public title: string;
  public icon: IIConType;
  public secondaryMessage: string;
  public approveButtonText: string;
  public cancelButtonText: string;
  public showCancelButton: boolean;
  public makeCancelPrimaryButton: boolean;
  public currentLanguage: string;
  public isLocaleSetting: boolean;
  public isContentTemplate: boolean;
  public isNegativeCTA: boolean;
  public dontShowAgainClickHandler: any;
  public readonly YELLOW_60: string = YELLOW_60;

  private _resolve: (value: unknown) => void;
  private _reject: () => void;
  private el: HTMLElement;
  private $modal: any;
  private promptHidden: boolean;
  private dashboardSettings: DashboardSettings;

  constructor(
    _el: ElementRef,
    @Inject(ConfirmPrompt) confirmPrompt,
    private router: Router,
  ) {
    this.el = _el.nativeElement;
    confirmPrompt.registerComponent(this);

    this.promptHidden = true;
  }

  public ngAfterViewInit() {
    const onApprove = this._onApprove.bind(this);
    const onReject = this._onReject.bind(this);
    this.$modal = $(this.el.querySelector('.ui.modal'));
    this.$modal.modal({
      onApprove,
      onDeny: onReject,
      onHide: onReject,
      allowMultiple: true,
    });
  }

  public show(message: string | TemplateRef<any>, {
    title = $localize`Confirm`,
    secondaryMessage = '',
    icon = null,
    approveButtonText = $localize`OK`,
    cancelButtonText = $localize`Cancel`,
    showCancelButton = true,
    isLocaleSetting = false,
    makeCancelPrimaryButton = false,
    isNegativeCTA = false,
    dashboardSettings= {} }: IConfirmPromptOptions = {},
  ) {
    this.message = message;
    this.title = title;
    this.secondaryMessage = secondaryMessage;
    this.icon = icon;
    this.approveButtonText = approveButtonText;
    this.cancelButtonText = cancelButtonText;
    this.showCancelButton = showCancelButton;
    this.makeCancelPrimaryButton = makeCancelPrimaryButton;
    this.isContentTemplate = this.message instanceof TemplateRef;
    this.isNegativeCTA = isNegativeCTA;
    this.isLocaleSetting = isLocaleSetting;
    this.dashboardSettings = dashboardSettings;

    // get language name only
    this.currentLanguage = SUPPORTED_LANGUAGES[getLocale()].split(' ')[0];

    this.$modal.modal('show');
    this.promptHidden = false;
    return new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }

  public hide() {
    this.$modal.modal('hide');
    this.promptHidden = true;
    this._onReject();
    this.setDashboardSettings(LOCALE_MODAL_DASHBOARD_KEYS.SHOW_SESSION, false);
  }

  public isPromptHidden() {
    return this.promptHidden;
  }

  public redirectToAccountSettings() {
    this.setDashboardSettings(LOCALE_MODAL_DASHBOARD_KEYS.SHOW_SESSION, false);
    seti18nLangCookie(getCookie(I18N_COOKIE_KEY), getLocale());
    this.hide();
    this.router.navigate(['/settings/account/edit']);
  }

  public dontShowLocaleSettingModalAgain(event: any) {
    this.setDashboardSettings(LOCALE_MODAL_DASHBOARD_KEYS.SHOW, !event.target.checked);
    seti18nLangCookie(getCookie(I18N_COOKIE_KEY), getLocale());
  }

  private _onApprove() {
    if (this._resolve) {
      this._resolve(true);
      this._resolve = this._reject = null;
    }
    this.promptHidden = true;
    this.setDashboardSettings(LOCALE_MODAL_DASHBOARD_KEYS.SHOW_SESSION, false);
  }

  private _onReject() {
    if (this._reject) {
      this._reject();
      this._reject = this._resolve = null;
    }
    this.promptHidden = true;
    this.setDashboardSettings(LOCALE_MODAL_DASHBOARD_KEYS.SHOW_SESSION, false);
  }

  private setDashboardSettings(key: string, value: boolean) {
    if (this.isLocaleSetting) {
      this.dashboardSettings.setKey(key, value);
    }
  }
}
